import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PrimaryNavigation } from '../../components/navigation/PrimaryNavigation';
import { NavigationProvider } from '../../components/navigation/NavigationProvider';
import Icon from '../../components/AppIcon';
import SummaryCard from './components/SummaryCard';
import GoalProgressCard from './components/GoalProgressCard';
import SavingsAllocationChart from './components/SavingsAllocationChart';
import RetirementProjectionChart from './components/RetirementProjectionChart';
import AlertNotification from './components/AlertNotification';
import FinancialHealthScore from './components/FinancialHealthScore';
import QuickActionPanel from './components/QuickActionPanel';
import { generateDashboardPDF } from '../../utils/pdfExport';
import Button from '../../components/ui/Button';


const FinancialDashboard = () => {
  const navigate = useNavigate();
  const [isCalculating, setIsCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [alerts, setAlerts] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalGoals: 0,
    retirementReadiness: 0,
    monthlySavingsRequired: '₹0',
    projectedWealth: '₹0'
  });
  const [goalsData, setGoalsData] = useState([]);
  const [savingsAllocationData, setSavingsAllocationData] = useState([]);
  const [retirementProjectionData, setRetirementProjectionData] = useState([]);

  // Fetch data from localStorage on mount
  useEffect(() => {
    const fetchDashboardData = () => {
      try {
        // Fetch life goals
        const lifeGoals = JSON.parse(localStorage.getItem('lifeGoals') || '[]');
        
        // Fetch retirement data
        const retirementData = JSON.parse(localStorage.getItem('retirementProjection') || 'null');
        
        // Calculate summary data
        const totalGoals = lifeGoals?.length || 0;
        const totalTargetAmount = lifeGoals?.reduce((sum, goal) => sum + (goal?.targetAmount || 0), 0);
        const totalCurrentSavings = lifeGoals?.reduce((sum, goal) => sum + (goal?.currentSavings || 0), 0);
        const totalMonthlySavings = lifeGoals?.reduce((sum, goal) => sum + (goal?.monthlySavingsRequired || 0), 0);
        
        // Calculate retirement readiness - including all savings from goals
        let retirementReadiness = 0;
        if (retirementData && retirementData?.totalCorpusRequired) {
          retirementReadiness = Math.min(
            Math.round((totalCurrentSavings / retirementData?.totalCorpusRequired) * 100),
            100
          );
        }

        // Format summary data - use retirement data if available, otherwise use calculated totals
        setSummaryData({
          totalGoals,
          retirementReadiness,
          monthlySavingsRequired: formatIndianCurrency(totalMonthlySavings),
          projectedWealth: retirementData ? formatIndianCurrency(retirementData?.totalCorpusRequired || 0) : formatIndianCurrency(totalTargetAmount)
        });

        // Transform life goals to dashboard format
        const transformedGoals = lifeGoals?.map(goal => {
          const progressPercentage = Math.round(((goal?.currentSavings || 0) / (goal?.targetAmount || 1)) * 100);
          
          return {
            id: goal?.id,
            name: goal?.name,
            category: goal?.type?.charAt(0)?.toUpperCase() + goal?.type?.slice(1),
            icon: getGoalIcon(goal?.type),
            targetAmount: formatIndianCurrency(goal?.targetAmount || 0),
            currentSavings: formatIndianCurrency(goal?.currentSavings || 0),
            timeline: calculateTimeline(goal?.targetDate),
            progress: progressPercentage,
            priority: getPriority(goal?.targetDate, progressPercentage),
            nextMilestone: getNextMilestone(goal)
          };
        });
        
        setGoalsData(transformedGoals);

        // Calculate savings allocation - include all goals from lifeGoals
        const allocation = [];
        
        lifeGoals?.forEach(goal => {
          allocation?.push({
            name: goal?.name,
            value: goal?.monthlySavingsRequired || 0,
            percentage: 0
          });
        });

        // Calculate percentages
        const totalAllocation = allocation?.reduce((sum, item) => sum + item?.value, 0);
        allocation?.forEach(item => {
          item.percentage = totalAllocation > 0 ? Math.round((item?.value / totalAllocation) * 100) : 0;
        });

        setSavingsAllocationData(allocation);

        // Generate retirement projection chart data
        if (retirementData) {
          const userData = JSON.parse(localStorage.getItem('userProfile') || '{}');
          const calculateCurrentAge = (dobString) => {
            if (!dobString) return 32;
            const [day, month, year] = dobString?.split('/')?.map(Number);
            const birthDate = new Date(year, month - 1, day);
            const today = new Date();
            let age = today?.getFullYear() - birthDate?.getFullYear();
            const monthDiff = today?.getMonth() - birthDate?.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
              age--;
            }
            return age;
          };

          const currentAge = userData?.dateOfBirth ? calculateCurrentAge(userData?.dateOfBirth) : 32;
          const retirementAge = 60;
          const yearsToRetirement = retirementAge - currentAge;
          
          const projectionYears = [];
          const currentYear = new Date()?.getFullYear();
          
          for (let i = 0; i <= yearsToRetirement; i += 3) {
            const year = currentYear + i;
            const progressRatio = i / yearsToRetirement;
            const projected = totalCurrentSavings + (progressRatio * (retirementData?.totalCorpusRequired - totalCurrentSavings));
            const required = progressRatio * retirementData?.totalCorpusRequired;
            
            projectionYears?.push({
              year,
              projected: Math.round(projected),
              required: Math.round(required)
            });
          }

          setRetirementProjectionData(projectionYears);
        }

        // Generate alerts
        const newAlerts = generateAlerts(lifeGoals, retirementData);
        setAlerts(newAlerts);

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchDashboardData();
  }, []);

  const getGoalIcon = (type) => {
    const iconMap = {
      'retirement': 'PiggyBank',
      'car': 'Car',
      'home': 'Home',
      'education': 'GraduationCap',
      'wedding': 'Heart',
      'custom': 'Target'
    };
    return iconMap?.[type] || 'Target';
  };

  const calculateTimeline = (targetDate) => {
    if (!targetDate) return 'N/A';
    const target = new Date(targetDate);
    const today = new Date();
    const yearsRemaining = Math.max(0, Math.ceil((target - today) / (365.25 * 24 * 60 * 60 * 1000)));
    return `${yearsRemaining} year${yearsRemaining !== 1 ? 's' : ''}`;
  };

  const getPriority = (targetDate, progress) => {
    if (!targetDate) return 'low';
    const target = new Date(targetDate);
    const today = new Date();
    const yearsRemaining = (target - today) / (365.25 * 24 * 60 * 60 * 1000);
    
    if (yearsRemaining < 3 || progress < 30) return 'high';
    if (yearsRemaining < 7 || progress < 60) return 'medium';
    return 'low';
  };

  const getNextMilestone = (goal) => {
    const progressPercentage = ((goal?.currentSavings || 0) / (goal?.targetAmount || 1)) * 100;
    const targetDate = new Date(goal?.targetDate);
    const yearsRemaining = Math.max(0, Math.ceil((targetDate - new Date()) / (365.25 * 24 * 60 * 60 * 1000)));
    
    if (progressPercentage < 25) {
      return `Reach 25% by ${new Date(new Date()?.setFullYear(new Date()?.getFullYear() + 1))?.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
    } else if (progressPercentage < 50) {
      return `Reach 50% by ${new Date(new Date()?.setFullYear(new Date()?.getFullYear() + Math.floor(yearsRemaining / 2)))?.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
    } else if (progressPercentage < 75) {
      return `Reach 75% by ${new Date(new Date()?.setFullYear(new Date()?.getFullYear() + Math.floor(yearsRemaining / 3)))?.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
    } else {
      return `Complete by ${targetDate?.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
    }
  };

  const generateAlerts = (goals, retirementData) => {
    const alerts = [];
    
    // Check for goal conflicts
    const goalsByYear = {};
    goals?.forEach(goal => {
      const year = new Date(goal?.targetDate)?.getFullYear();
      if (!goalsByYear?.[year]) {
        goalsByYear[year] = [];
      }
      goalsByYear?.[year]?.push(goal?.name);
    });

    Object.entries(goalsByYear)?.forEach(([year, goalNames]) => {
      if (goalNames?.length > 1) {
        alerts?.push({
          id: Date.now() + Math.random(),
          type: 'warning',
          title: 'Goal Conflict Detected',
          message: `Multiple goals (${goalNames?.join(', ')}) target year ${year}. Consider adjusting timelines.`,
          action: 'adjust-timeline',
          actionLabel: 'Adjust Timeline'
        });
      }
    });

    // Check retirement readiness
    if (retirementData) {
      const readiness = Math.round((goals?.reduce((sum, g) => sum + (g?.currentSavings || 0), 0) / retirementData?.totalCorpusRequired) * 100);
      if (readiness >= 50) {
        alerts?.push({
          id: Date.now() + Math.random() + 1,
          type: 'success',
          title: 'Milestone Achieved',
          message: `Congratulations! You have reached ${readiness}% of your retirement corpus target.`,
          action: null
        });
      }
    }

    // Check for low progress goals
    goals?.forEach(goal => {
      const progress = ((goal?.currentSavings || 0) / (goal?.targetAmount || 1)) * 100;
      const yearsRemaining = Math.ceil((new Date(goal?.targetDate) - new Date()) / (365.25 * 24 * 60 * 60 * 1000));
      
      if (progress < 20 && yearsRemaining < 5) {
        alerts?.push({
          id: Date.now() + Math.random() + 2,
          type: 'info',
          title: 'Plan Adjustment Needed',
          message: `Your "${goal?.name}" goal needs increased monthly savings to stay on track.`,
          action: 'adjust-savings',
          actionLabel: 'Review Plan'
        });
      }
    });

    return alerts;
  };

  const formatIndianCurrency = (amount) => {
    return '₹' + (amount || 0)?.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  const handleExportPlan = async () => {
    try {
      // Get user data
      const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      const retirementData = JSON.parse(localStorage.getItem('retirementProjection') || 'null');
      
      const calculateCurrentAge = (dobString) => {
        if (!dobString) return 32;
        const [day, month, year] = dobString?.split('/')?.map(Number);
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today?.getFullYear() - birthDate?.getFullYear();
        const monthDiff = today?.getMonth() - birthDate?.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
          age--;
        }
        return age;
      };

      const userData = {
        name: userProfile?.fullName || 'User',
        currentAge: userProfile?.dateOfBirth ? calculateCurrentAge(userProfile?.dateOfBirth) : 32,
        fullName: userProfile?.fullName || userProfile?.name || '-',
        email: userProfile?.email || '-',
        dateOfBirth: userProfile?.dateOfBirth || '-'
      };

      const dashboardData = {
        summary: summaryData,
        goals: goalsData,
        savingsAllocation: savingsAllocationData,
        retirementProjection: retirementProjectionData,
        alerts: alerts,
        retirementData: retirementData
      };

      // Ask user for export preference
      const useEnhanced = window.confirm(
        'Would you like to export the PDF with exact web page design?\n\n' +
        'Click "OK" for visual design matching (larger file size)\n' +
        'Click "Cancel" for standard formatted PDF (smaller file size)'
      );

      const result = await generateDashboardPDF(userData, dashboardData, useEnhanced);
      
      if (result?.success) {
        alert(`PDF exported successfully: ${result?.fileName}`);
      } else {
        alert(`Failed to export PDF: ${result?.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error exporting dashboard PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  const handleRecalculate = () => {
    setIsCalculating(true);
    setTimeout(() => {
      setIsCalculating(false);
      setAlerts([...alerts, {
        id: Date.now(),
        type: 'success',
        title: 'Recalculation Complete',
        message: 'Your financial projections have been updated based on current data.',
        action: null
      }]);
    }, 2000);
  };

  const handleDismissAlert = (alertId) => {
    setAlerts(alerts?.filter(alert => alert?.id !== alertId));
  };

  const handleAlertAction = (action) => {
    if (action === 'adjust-timeline' || action === 'adjust-savings') {
      navigate('/life-goals-management');
    }
  };

  const handleAddGoal = () => {
    navigate('/life-goals-management');
  };

  const handleModifyGoals = () => {
    navigate('/life-goals-management');
  };

  const financialHealthRecommendations = [
    {
      icon: 'TrendingUp',
      title: 'Keep stepping up your Investments Annually',
      description: ''
    },
    {
      icon: 'PieChart',
      title: 'Maintain 40% and above savings rate',
      description: ''
    },
    {
      icon: 'Shield',
      title: 'Take Adequate insurance cover for Health and Terminal Insurance',
      description: ''
    }
  ];

  const handleViewGoal = (goalId) => {
    navigate('/life-goals-management', { state: { selectedGoalId: goalId } });
  };

  const handleModifyGoal = (goalId) => {
    navigate('/life-goals-management', { state: { editGoalId: goalId } });
  };

  useEffect(() => {
    const isAuth = localStorage.getItem('isAuthenticated');
    if (isAuth !== 'true') {
      navigate('/user-registration');
    }
  }, [navigate]);

  return (
    <NavigationProvider>
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        
        <main className="financial-dashboard-page main-content main-content-desktop">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-3xl font-bold text-foreground">Financial Dashboard</h1>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  iconName="FileText"
                  iconPosition="left"
                  onClick={handleExportPlan}
                >
                  Export PDF
                </Button>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Calendar" size={16} />
                  <span>Last updated: {new Date()?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
              </div>
            </div>
            <p className="text-muted-foreground">Comprehensive overview of your retirement planning and life goals progress</p>
          </div>

          {alerts?.length > 0 && (
            <div className="mb-8">
              <AlertNotification 
                alerts={alerts}
                onDismiss={handleDismissAlert}
                onAction={handleAlertAction}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <SummaryCard
              title="Total Financial Goals"
              value={summaryData?.totalGoals}
              subtitle="Active goals being tracked"
              icon="Target"
              variant="default"
              trend={null}
              trendValue={null}
            />
            <SummaryCard
              title="Retirement Readiness"
              value={`${summaryData?.retirementReadiness}%`}
              subtitle="On track for retirement"
              icon="TrendingUp"
              trend="up"
              trendValue="+5%"
              variant="success"
            />
            <SummaryCard
              title="Monthly Savings Required"
              value={summaryData?.monthlySavingsRequired}
              subtitle="To achieve all goals"
              icon="Wallet"
              variant="primary"
              trend={null}
              trendValue={null}
            />
            <SummaryCard
              title="Projected Wealth"
              value={summaryData?.projectedWealth}
              subtitle="At retirement age"
              icon="TrendingUp"
              trend="up"
              trendValue="+12%"
              variant="success"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Financial Insights</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeTab === 'overview' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    Overview
                  </button>
                  <button
                    onClick={() => setActiveTab('projection')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeTab === 'projection' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    Projection
                  </button>
                </div>
              </div>
              
              {activeTab === 'overview' ? (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Savings Allocation</h3>
                  <SavingsAllocationChart data={savingsAllocationData} />
                </div>
              ) : (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Retirement Corpus Growth</h3>
                  <RetirementProjectionChart data={retirementProjectionData} />
                </div>
              )}
            </div>

            <div className="space-y-6">
              <FinancialHealthScore 
                score={72}
                recommendations={financialHealthRecommendations}
              />
              <QuickActionPanel
                onAddGoal={handleAddGoal}
                onModifyGoals={handleModifyGoals}
                onExportPlan={handleExportPlan}
                onRecalculate={handleRecalculate}
                isCalculating={isCalculating}
              />
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">Active Goals Overview</h2>
              <button
                onClick={handleAddGoal}
                className="text-sm text-primary hover:text-primary/80 font-medium transition-colors flex items-center gap-2"
              >
                <Icon name="Plus" size={16} />
                Add New Goal
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {goalsData?.map((goal) => (
                <GoalProgressCard
                  key={goal?.id}
                  goal={goal}
                  onView={handleViewGoal}
                  onModify={handleModifyGoal}
                />
              ))}
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Lightbulb" size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">Financial Planning Tip</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Review your financial goals quarterly to ensure they align with your current life situation and market conditions. Small adjustments today can lead to significant improvements in achieving your long-term objectives.
                </p>
                <button
                  onClick={() => navigate('/retirement-planning-setup')}
                  className="text-sm text-primary hover:text-primary/80 font-medium transition-colors flex items-center gap-2"
                >
                  Review Planning Setup
                  <Icon name="ArrowRight" size={16} />
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </NavigationProvider>
  );
};

export default FinancialDashboard;